import { useCallback } from 'react';

import { useLazyQuery } from '@apollo/client/react';
import { Box, FormControl, FormHelperText, TextField } from '@mui/material';
import { type Control, Controller } from 'react-hook-form';

import { gql } from '@graphql/__generated__';
import type {
  Emails_Bool_Exp,
  MultiEmailsFieldFragment,
} from '@graphql/__generated__/graphql';
import { MultiContactInfoField } from '@modules/_shared/components/MultiContactInfoField';
import { isEmailValid } from '@modules/_shared/utils/email-validation';
import { useLocale } from '@shared/hooks/locale';

export const MULTI_EMAILS_FIELD_FRAGMENT = gql(/* GraphQL */ `
  fragment MultiEmailsField on emails {
    id
    email
    verified
    primary
  }
`);

const CHECK_EMAIL_UNICITY = gql(/* GraphQL */ `
  query CheckEmailUnicity($checks: emails_bool_exp!) {
    emails(where: $checks, limit: 1) {
      id
    }
  }
`);

type MultiEmailsFieldProps = {
  label?: string;
  required?: boolean;
  parentUnicityCheck?:
    | { organisation_id: string }
    | { user_id: string }
    | { team_id: string };
};

type EmailItem = Omit<MultiEmailsFieldFragment, 'email'> & {
  value: string;
  email_id?: string;
};

export type MultiEmailsFieldFormData = {
  emails: Array<Omit<EmailItem, 'id'>>;
};

const EmailEditForm: React.FC<{
  control: Control<MultiEmailsFieldFormData>;
  name: `emails.${number}`;
}> = ({ control, name }) => {
  const { t } = useLocale();

  return (
    <Box sx={{ flexGrow: 1 }}>
      <Controller
        name={`${name}.value`}
        control={control}
        rules={{
          required: t('This field is required'),
        }}
        render={({ field, fieldState: { error } }) => (
          <FormControl fullWidth error={!!error}>
            <TextField
              {...field}
              size="small"
              variant="outlined"
              type="email"
              autoComplete="email"
              slotProps={{
                input: {
                  sx: { background: 'var(--mui-palette-background-paper)' },
                },
              }}
              fullWidth
            />
            <FormHelperText>{error?.message}</FormHelperText>
          </FormControl>
        )}
      />
    </Box>
  );
};

export const MultiEmailsField: React.FC<MultiEmailsFieldProps> = ({
  label,
  required,
  parentUnicityCheck,
}) => {
  const { t } = useLocale();
  const [checkEmailUnicity] = useLazyQuery(CHECK_EMAIL_UNICITY);

  const validateValue = useCallback(
    async (item: EmailItem) => {
      if (!isEmailValid(item.value)) {
        return {
          message: t('Invalid email'),
          type: 'validate',
        };
      }

      if (parentUnicityCheck == null) {
        return null;
      }

      const { data: checkEmailUnicityData } = await checkEmailUnicity({
        variables: {
          checks: {
            ...Object.entries(parentUnicityCheck).reduce<
              Pick<Emails_Bool_Exp, 'organisation_id' | 'user_id' | 'team_id'>
            >((acc, [key, value]) => {
              acc[key as 'organisation_id' | 'user_id' | 'team_id'] = {
                _eq: value,
              };
              return acc;
            }, {}),
            email: { _eq: item.value },
            id: {
              _nin: item.email_id != null ? [item.email_id] : [],
            },
          },
        },
      });

      if ((checkEmailUnicityData?.emails.length ?? 0) > 0) {
        return {
          message: t('Email already exists'),
          type: 'server',
        };
      }

      return null;
    },
    [checkEmailUnicity, parentUnicityCheck, t],
  );

  return (
    <MultiContactInfoField
      label={label ?? t('Emails')}
      requiredMessage={
        required ? t('At least one email is required') : undefined
      }
      fieldName="emails"
      UpdateForm={EmailEditForm as any}
      addLabel={t('Add email')}
      validateValue={validateValue}
    />
  );
};
